import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full bg-transparent border border-[rgba(197,165,90,0.3)] rounded-md px-4 py-3 text-[#F5F0E8] placeholder:text-[#A0988E]/60 focus:outline-none focus:border-[#D4AF37]";

export default function ThankYouNoteForm({ token, familyName, existing, onSaved }) {
  const [form, setForm] = useState({
    display_name: existing?.display_name || familyName || "",
    place: existing?.place || "",
    message: existing?.message || "",
    rating: existing?.rating || 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 5) return setError("Please write a few words.");
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/film/${token}/review`, { ...form, rating: form.rating || null });
      onSaved(data);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail?.toString?.() || "Could not send your note. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (sent) {
    return (
      <section data-testid="note-sent" className="card-surface p-7 text-center">
        <CheckCircle2 size={26} className="mx-auto text-[#D4AF37]" strokeWidth={1.4} />
        <h3 className="serif text-2xl text-[#F5F0E8] mt-3">Thank you for your kind words</h3>
        <p className="mt-2 text-sm text-[#A0988E] spectral">They mean more than we can say.</p>
        <button data-testid="note-edit-again" onClick={() => setSent(false)} className="mt-4 text-xs text-[#A0988E] hover:text-[#F5F0E8]">Edit my note</button>
      </section>
    );
  }

  return (
    <form data-testid="thank-you-note-form" onSubmit={submit} className="card-surface p-6 sm:p-7">
      <div className="text-[11px] tracking-widest uppercase text-[#C5A55A]/80">Leave a thank-you note</div>
      <p className="mt-3 text-sm text-[#A0988E] spectral leading-relaxed">
        If the film meant something to you, we&apos;d be honoured to hear it. With your permission it may be shared on our homepage to comfort other families.
      </p>
      {existing && (
        <p data-testid="note-status" className="mt-3 text-xs text-[#D4AF37]">
          {{ pending: "We've received your note. It will appear on our homepage once we've read it.",
             approved: "Your note is shared on our homepage. Thank you.",
             hidden: "We've received your note with thanks." }[existing.status]}
        </p>
      )}
      <div className="mt-5 flex items-center gap-1" role="radiogroup" aria-label="Rating (optional)">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`note-star-${n}`} aria-label={`${n} star${n > 1 ? "s" : ""}`}
                  onClick={() => setForm({ ...form, rating: form.rating === n ? 0 : n })} className="p-1">
            <Star size={20} className={n <= form.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-[#A0988E]/50"} />
          </button>
        ))}
        <span className="ml-2 text-xs text-[#A0988E]">optional</span>
      </div>
      <textarea data-testid="note-message-input" rows={4} maxLength={600} value={form.message} onChange={set("message")}
                placeholder="A few words about the film…" className={`${field} mt-4 resize-none`} />
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <input data-testid="note-name-input" maxLength={60} value={form.display_name} onChange={set("display_name")} placeholder="Sign it as (e.g. The Torres Family)" className={field} />
        <input data-testid="note-place-input" maxLength={60} value={form.place} onChange={set("place")} placeholder="City, State (optional)" className={field} />
      </div>
      {error && <p data-testid="note-error" className="mt-3 text-sm text-[#E6B89C]">{error}</p>}
      <button data-testid="note-submit-button" disabled={saving} className="btn-primary mt-5 disabled:opacity-60">
        {saving && <Loader2 size={15} className="animate-spin" />} {existing ? "Update my note" : "Send our note"}
      </button>
    </form>
  );
}
